import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Sparkles, Copy, Check, ShoppingBag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "@/components/SEO";

/**
 * /unwrap — drag-to-tear gift-wrap experience that rewards visitors with
 * a 10% off code when they "unwrap" the gift. Shareable + viral hook:
 * "drag your finger across the wrap to unwrap it." Rebroadcasts as SEO
 * for queries like "booty breeze promo code", "10% off booty breeze",
 * "free booty breeze coupon".
 */

const DISCOUNT_CODE = "UNWRAP10";
const REVEAL_THRESHOLD = 0.55;   // % of tear points crossed before we open the box

export default function UnwrapForDiscount() {
  const [progress, setProgress] = useState(0);   // 0..1 how much has been torn
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const boxRef = useRef(null);
  const draggingRef = useRef(false);
  const seenCellsRef = useRef(new Set());

  const GRID = 12;   // 12x12 tear grid — enough resolution to feel real

  const handleMove = (clientX, clientY) => {
    if (!draggingRef.current || revealed) return;
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    // Central band gets more weight (as if the paper tears open from the middle)
    const cx = Math.floor(x * GRID);
    const cy = Math.floor(y * GRID);
    // 3x3 neighbourhood so a fingertip drag makes real tears
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = cx + dx, ny = cy + dy;
        if (nx >= 0 && nx < GRID && ny >= 0 && ny < GRID) {
          seenCellsRef.current.add(`${nx},${ny}`);
        }
      }
    }
    const pct = seenCellsRef.current.size / (GRID * GRID);
    setProgress(pct);
    if (pct >= REVEAL_THRESHOLD) {
      setRevealed(true);
      draggingRef.current = false;
    }
  };

  useEffect(() => {
    const onMove = (e) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches.length) handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  const copyCode = () => {
    navigator.clipboard?.writeText(DISCOUNT_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "PromotionOffer",
    "name": "10% off first Booty Breeze",
    "url": "https://bootybreeze.com/unwrap",
    "priceSpecification": { "@type": "PriceSpecification", "priceCurrency": "USD" },
    "eligibleCustomerType": "FirstTimeCustomer",
    "discountCode": DISCOUNT_CODE,
    "description": "Unwrap the gift on bootybreeze.com/unwrap to reveal a 10% off code for your first order.",
  }];

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-14" data-testid="unwrap-page">
      <SEO
        title="Unwrap for 10% Off · Booty Breeze Promo Code"
        description="Drag your finger across the gift wrap to unwrap it and reveal a 10% off Booty Breeze promo code. Free shipping on 2+."
        path="/unwrap"
        jsonLd={jsonLd}
      />

      <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-3">Interactive · New visitor treat</div>
      <h1 className="font-display text-4xl sm:text-6xl text-white mb-3">
        Unwrap the <span className="text-neon-pink">gift.</span>
      </h1>
      <p className="text-white/60 mb-8 max-w-xl">
        Drag your finger across the wrapping paper. Tear it up. Reveal your reward.
      </p>

      <div className="relative aspect-square max-w-xl mx-auto select-none" style={{ touchAction: "none" }} data-testid="unwrap-box"
           ref={boxRef}
           onPointerDown={(e) => { draggingRef.current = true; handleMove(e.clientX, e.clientY); }}
           onTouchStart={(e) => { draggingRef.current = true; if (e.touches[0]) handleMove(e.touches[0].clientX, e.touches[0].clientY); }}>
        {/* Reward layer — sits behind the wrap */}
        <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF3D8B] via-[#A855F7] to-[#3B82F6] flex items-center justify-center overflow-hidden ${revealed ? "" : "blur-[1px]"}`} data-testid="unwrap-reward">
          <div className="text-center px-6">
            <Sparkles className="mx-auto mb-3 text-black" size={40} />
            <div className="uppercase text-black/70 text-[10px] tracking-widest">Your reward</div>
            <div className="font-display text-6xl sm:text-7xl text-black leading-none mt-2">10% OFF</div>
            <div className="uppercase text-black/70 text-xs tracking-widest mt-3">Code</div>
            <div className="font-mono text-2xl sm:text-3xl text-black font-bold mt-1 tracking-widest" data-testid="unwrap-code-hidden">{DISCOUNT_CODE}</div>
          </div>
        </div>

        {/* Wrapping paper — SVG grid whose cells fade + rotate as they're "torn". */}
        {!revealed && (
          <svg viewBox={`0 0 ${GRID * 10} ${GRID * 10}`} className="absolute inset-0 w-full h-full">
            <defs>
              <pattern id="stripe" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                <rect width="6" height="6" fill="#FF3D8B" />
                <rect x="0" width="3" height="6" fill="#A855F7" />
              </pattern>
            </defs>
            {Array.from({ length: GRID * GRID }).map((_, i) => {
              const cx = i % GRID;
              const cy = Math.floor(i / GRID);
              const torn = seenCellsRef.current.has(`${cx},${cy}`);
              return (
                <rect
                  key={i}
                  x={cx * 10}
                  y={cy * 10}
                  width={10}
                  height={10}
                  fill="url(#stripe)"
                  opacity={torn ? 0 : 1}
                  style={{ transformOrigin: `${cx * 10 + 5}px ${cy * 10 + 5}px`, transition: "opacity 260ms ease-out" }}
                />
              );
            })}
            {/* ribbon */}
            <rect x={GRID * 5 - 4} y={0} width={8} height={GRID * 10} fill="#FFD6E3" opacity={progress > 0.4 ? 0 : 1} style={{ transition: "opacity 300ms" }} />
            <rect x={0} y={GRID * 5 - 4} width={GRID * 10} height={8} fill="#FFD6E3" opacity={progress > 0.4 ? 0 : 1} style={{ transition: "opacity 300ms" }} />
          </svg>
        )}

        {!revealed && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-widest bg-black/70 rounded-full px-4 py-1.5" data-testid="unwrap-hint">
            {progress > 0 ? `${Math.round(progress * 100)}% torn` : "Drag your finger across ↔"}
          </div>
        )}
      </div>

      <AnimatePresence>
        {revealed && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-8 rounded-2xl border border-[#FF3D8B]/40 bg-[#0f0f0f] p-6 max-w-xl mx-auto" data-testid="unwrap-revealed">
            <div className="text-white/50 text-[10px] uppercase tracking-widest mb-2">Copy this code at checkout</div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="font-mono text-3xl text-white font-bold tracking-widest" data-testid="unwrap-code-value">{DISCOUNT_CODE}</div>
              <button onClick={copyCode} data-testid="unwrap-copy"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 hover:border-white/40 text-white/80 text-xs uppercase tracking-widest">
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
              </button>
            </div>
            <div className="text-white/60 text-sm mt-4">
              Good for 10% off your first Booty Breeze order. Ships free on 2 or more.
            </div>
            <Link to="/shop" data-testid="unwrap-shop-cta"
                  className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0]">
              <ShoppingBag size={15} /> Shop now →
            </Link>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
